import type { PointerEvent as ReactPointerEvent } from 'react';
import { useCallback, useRef } from 'react';

import { isFromPortal } from '@/lib/portalEvent';

import { useLatest } from './useLatest';

/**
 * usePointerDrag — one drag implementation for mouse AND touch.
 *
 * Pointer Events + pointer capture: after `setPointerCapture` the pointer
 * stays with the pressed element even when it leaves it (or the window), so
 * touch "just works" (iOS 13+).
 *
 *   const { onPointerDown } = usePointerDrag({ onStart, onMove, onEnd, onCancel });
 *   <div {...{ onPointerDown }} className="touch-none" />
 *
 * - Callbacks receive the raw PointerEvent (use `clientX/clientY`,
 *   `pointerType`). They are read through a ref, so passing fresh closures
 *   every render is fine and no listener re-attachment happens mid-drag.
 * - `onStart` may return `false` to reject the drag (guards).
 * - Non-primary pointers and non-left mouse buttons are ignored — a second
 *   touch during a drag does not start a second drag.
 * - `pointercancel` (iOS fires it when the browser steals the gesture, e.g.
 *   a system edge swipe) uses `onCancel` when supplied. Existing consumers
 *   without one retain the historical `onEnd` fallback.
 *
 * The drag reads its events on the window, by pointer id. The browser drops
 * the capture when React moves or replaces the pressed element during the
 * drag, and the release then goes to a different element. For the same
 * reason, a new press of the pointer, or a mouse move with no button down,
 * cancels a drag whose release was lost.
 *
 * The surface usually also needs `touch-action: none` (Tailwind `touch-none`)
 * so the browser doesn't claim the gesture for scrolling first; use
 * `touch-pan-y` instead when vertical scrolling over the surface must keep
 * working.
 */
/**
 * What a press belongs to rather than to the surface under it. A drag that
 * captures the pointer swallows the click that follows, so a control pressed
 * inside a draggable surface never hears it - `data-no-drag` marks anything
 * else a host wants left alone.
 */
export const kNoDragSelector = 'button, a, input, select, textarea, [data-no-drag]';

/**
 * What a surface hands the hook. The press that opens a drag comes through
 * React, so `onStart` takes a synthetic event; every event after it comes
 * from the window and is the DOM's own.
 * `onStart` returning false rejects the drag.
 */
interface PointerDragHandlers<E extends HTMLElement = HTMLElement> {
    onStart?: (event: ReactPointerEvent<E>) => boolean | void;
    onMove?: (event: PointerEvent) => void;
    onEnd?: (event: PointerEvent) => void;
    onCancel?: (event: PointerEvent) => void;
}

/** Neither handler depends on the element the press came from. */
type FinishHandlers = Pick<PointerDragHandlers, 'onEnd' | 'onCancel'>;

/** The events that end a drag; `pointerdown` is the next press after a lost release. */
const FINISH_EVENTS = ['pointerup', 'pointercancel', 'pointerdown'] as const;

/** Only a release commits the drag. Each other end of it is a cancel. */
export function dispatchPointerFinish(
    { onEnd, onCancel }: FinishHandlers, event: PointerEvent,
): void {
    if (event.type === 'pointerup') { return onEnd?.(event); }
    return (onCancel ?? onEnd)?.(event);
}

export function usePointerDrag<E extends HTMLElement = HTMLElement>(
    { onStart, onMove, onEnd, onCancel }: PointerDragHandlers<E>,
): { onPointerDown: (event: ReactPointerEvent<E>) => void } {
    const cbRef = useLatest<PointerDragHandlers<E>>({ onStart, onMove, onEnd, onCancel });
    const activePointerRef = useRef<number | null>(null);

    const onPointerDown = useCallback((event: ReactPointerEvent<E>): void => {
        if (!event.isPrimary) { return; }
        if (event.pointerType === 'mouse' && event.button !== 0) { return; }
        // A press in a menu or dialog portalled out of the surface bubbles here
        // through the React tree; capturing on it would steal the click that
        // follows from whatever was pressed.
        if (isFromPortal(event)) { return; }
        if (activePointerRef.current !== null) { return; }

        if (cbRef.current.onStart?.(event) === false) { return; }

        const element = event.currentTarget;
        const { pointerId } = event;
        activePointerRef.current = pointerId;
        try { element.setPointerCapture(pointerId); } catch { /* node mid-detach */ }

        const listeningAbortController = new AbortController();
        const finish = (endedEvent: PointerEvent): void => {
            listeningAbortController.abort();
            try { element.releasePointerCapture(pointerId); } catch { /* already released */ }
            activePointerRef.current = null;
            dispatchPointerFinish(cbRef.current, endedEvent);
        };
        const move = (movedEvent: PointerEvent): void => {
            if (movedEvent.pointerId !== pointerId) { return; }
            // The button is up, so the release went where the window does not see it.
            if (movedEvent.pointerType === 'mouse' && movedEvent.buttons === 0) {
                finish(movedEvent);
                return;
            }
            cbRef.current.onMove?.(movedEvent);
        };
        const end = (endedEvent: PointerEvent): void => {
            if (endedEvent.pointerId === pointerId) { finish(endedEvent); }
        };
        // Capture phase, so a handler that stops an event on its way down cannot
        // hide it from the drag. A listener added now does not hear the press
        // that is in dispatch.
        const options = { capture: true, signal: listeningAbortController.signal };
        window.addEventListener('pointermove', move, options);
        for (const type of FINISH_EVENTS) { window.addEventListener(type, end, options); }
    }, [cbRef]);

    return { onPointerDown };
}
